import React, { useState } from 'react';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Contract } from '../../data/mockContracts';
import { useAppStore } from '../../contexts/AppStore';

interface ContractFormProps {
  onSubmit: (contract: Omit<Contract, 'id'>) => void;
  onCancel: () => void;
}

export const ContractForm: React.FC<ContractFormProps> = ({ onSubmit, onCancel }) => {
  const { projects, stakeholders } = useAppStore();
  
  const [formData, setFormData] = useState({
    title: '',
    type: 'Licencia' as Contract['type'],
    status: 'Borrador' as Contract['status'],
    stakeholderId: '',
    projectId: '',
    startDate: '',
    endDate: '',
    value: '',
    currency: 'USD',
    description: '',
  });
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedStakeholder = stakeholders.find(s => s.id === formData.stakeholderId);
    const selectedProject = projects.find(p => p.id === formData.projectId);
    
    if (!selectedStakeholder || !selectedProject) {
      alert('Por favor selecciona un stakeholder y un proyecto válidos');
      return;
    }
    
    const newContract: Omit<Contract, 'id'> = {
      title: formData.title,
      type: formData.type,
      status: formData.status,
      stakeholderId: formData.stakeholderId,
      stakeholderName: selectedStakeholder.name,
      projectId: formData.projectId,
      projectName: selectedProject.title,
      startDate: new Date(formData.startDate).toISOString(),
      endDate: new Date(formData.endDate).toISOString(),
      value: parseFloat(formData.value) || 0,
      currency: formData.currency,
      description: formData.description,
    };
    
    onSubmit(newContract);
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Título del Contrato *"
        name="title"
        value={formData.title}
        onChange={handleChange}
        placeholder="Ej: Licencia Exclusiva - Sistema de Diagnóstico IA"
        required
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de Contrato *
          </label>
          <select
            name="type"
            value={formData.type}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="Licencia">Licencia</option>
            <option value="NDA">NDA</option>
            <option value="Colaboración">Colaboración</option>
            <option value="Transferencia">Transferencia</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Estado *
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="Borrador">Borrador</option>
            <option value="En Negociación">En Negociación</option>
            <option value="Firmado">Firmado</option>
            <option value="Vigente">Vigente</option>
            <option value="Vencido">Vencido</option>
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Stakeholder *
          </label>
          <select
            name="stakeholderId"
            value={formData.stakeholderId}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">Seleccionar stakeholder...</option>
            {stakeholders.map((stakeholder) => (
              <option key={stakeholder.id} value={stakeholder.id}>
                {stakeholder.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Proyecto Vinculado *
          </label>
          <select
            name="projectId"
            value={formData.projectId}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">Seleccionar proyecto...</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.title}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Fecha de Inicio *"
          type="date"
          name="startDate"
          value={formData.startDate}
          onChange={handleChange}
          required
        />
        
        <Input
          label="Fecha de Finalización *"
          type="date"
          name="endDate"
          value={formData.endDate}
          onChange={handleChange}
          required
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Valor del Contrato"
          type="number"
          name="value"
          value={formData.value}
          onChange={handleChange}
          placeholder="0"
          min="0"
          step="1000"
        />
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Moneda
          </label>
          <select
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="USD">USD - Dólar</option>
            <option value="COP">COP - Peso Colombiano</option>
            <option value="EUR">EUR - Euro</option>
          </select>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Descripción
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Descripción detallada del contrato, términos especiales, regalías, etc..."
        />
      </div>
      
      <div className="flex justify-end space-x-3 pt-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary">
          Registrar Contrato
        </Button>
      </div>
    </form>
  );
};
